import { useCallback, useEffect, useRef, useState } from "react";
import api from "./api";
import { uploadFile } from "./uploads";

/**
 * useVoiceCapture — Wave 19 #107.
 *
 * Drives the mic recording + upload pipeline behind <VoiceTextarea>.
 * Returns:
 *   {
 *     recording, elapsedSec, level (0..1),
 *     start(), stop(), cancel(),
 *     uploading, error, result   // result = { raw, refined, extracted }
 *   }
 *
 * Uses the existing chat-style audio capture (MediaRecorder → upload to
 * the backend transcribe-refine endpoint). Browser permission is
 * requested lazily on `start()`.
 */
export default function useVoiceCapture({ context = "lead_activity", extraContext = "" } = {}) {
  const [recording, setRecording] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [level, setLevel] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  // Iter 371 — Voice notes must ALSO be stored as playable audio.
  // `audioUpload` is set as soon as the raw audio blob finishes
  // uploading (usually a second after stop, well before Whisper).
  // Consumers show an <audio controls> player immediately, then swap
  // in the transcription when `result` resolves.
  const [audioUpload, setAudioUpload] = useState(null); // {id, url, mime, duration_sec, size}

  const mediaStreamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);

  const reset = useCallback(() => {
    setError("");
    setResult(null);
    setAudioUpload(null);
    setElapsedSec(0);
    setLevel(0);
  }, []);

  const teardown = useCallback(() => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (analyserRef.current) { try { analyserRef.current.disconnect(); } catch (_e) { /* noop */ } analyserRef.current = null; }
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch (_e) { /* noop */ } audioCtxRef.current = null; }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => { try { t.stop(); } catch (_e) { /* noop */ } });
      mediaStreamRef.current = null;
    }
    recorderRef.current = null;
    chunksRef.current = [];
  }, []);

  useEffect(() => () => teardown(), [teardown]);

  const start = useCallback(async () => {
    reset();
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError("Mic access is not supported in this browser.");
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      // Pick a MIME the browser supports
      const candidates = [
        "audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg",
      ];
      const mime = candidates.find((c) => window.MediaRecorder?.isTypeSupported?.(c)) || "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.start(250);
      setRecording(true);
      // Elapsed timer
      const t0 = performance.now();
      tickRef.current = setInterval(() => {
        setElapsedSec(Math.floor((performance.now() - t0) / 1000));
      }, 250);
      // Audio level meter (cheap, animates the mic button)
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) {
          const ctx = new AC();
          audioCtxRef.current = ctx;
          const src = ctx.createMediaStreamSource(stream);
          const an = ctx.createAnalyser();
          an.fftSize = 256;
          src.connect(an);
          analyserRef.current = an;
          const buf = new Uint8Array(an.frequencyBinCount);
          const tick = () => {
            if (!analyserRef.current) return;
            an.getByteFrequencyData(buf);
            let sum = 0;
            for (let i = 0; i < buf.length; i++) sum += buf[i];
            const avg = sum / buf.length / 255;
            setLevel(Math.min(1, avg * 1.6));
            rafRef.current = requestAnimationFrame(tick);
          };
          rafRef.current = requestAnimationFrame(tick);
        }
      } catch (_e) { /* noop — level meter is optional */ }
    } catch (e) {
      setError(e?.message || "Microphone permission denied.");
      setRecording(false);
      teardown();
    }
  }, [reset, teardown]);

  const stop = useCallback(async () => {
    const rec = recorderRef.current;
    if (!rec) { teardown(); setRecording(false); return; }
    // Wait for the final dataavailable, then upload
    const stopped = new Promise((resolve) => {
      rec.onstop = () => resolve();
    });
    try { rec.stop(); } catch (_e) { /* noop */ }
    await stopped;
    const mime = rec.mimeType || "audio/webm";
    const blob = new Blob(chunksRef.current, { type: mime });
    const dur = elapsedSec;
    setRecording(false);
    teardown();
    if (!blob || blob.size === 0) {
      setError("No audio was captured.");
      return;
    }
    setUploading(true);

    // Iter 371 — Fire the raw-audio upload and Whisper transcribe in
    // PARALLEL so the player is available before transcription
    // completes. Both promises resolve independently.
    const ext = mime.includes("mp4") ? "mp4" : mime.includes("ogg") ? "ogg" : "webm";
    const filename = `voice-${new Date().toISOString().replace(/[:.]/g, "-")}.${ext}`;
    const audioFile = new File([blob], filename, { type: mime });

    const uploadPromise = uploadFile(audioFile, { source: "voice_note", label: `Voice note · ${dur}s` })
      .then((uploaded) => {
        const info = {
          id: uploaded?.id,
          url: uploaded?.url || `/api/uploads/${uploaded?.id}`,
          mime,
          duration_sec: dur,
          size: blob.size,
          filename,
        };
        setAudioUpload(info);
        return info;
      })
      .catch((e) => {
        // Non-fatal: transcription can still proceed. Surface a soft warning.
        // We keep going so the transcript still returns.
        console.warn("Voice audio upload failed:", e);
        return null;
      });

    try {
      const fd = new FormData();
      fd.append("audio", blob, filename);
      fd.append("context", context);
      if (extraContext) fd.append("extra_context", extraContext);
      const [{ data }, upInfo] = await Promise.all([
        api.post("/voice/transcribe-refine", fd, {
          timeout: 180000, // 3 min — Whisper + Claude refine round-trip
          headers: { "Content-Type": "multipart/form-data" },
        }),
        uploadPromise,
      ]);
      setResult({
        ...data,
        duration_sec: dur,
        voice_upload_id: upInfo?.id || null,
        voice_url: upInfo?.url || null,
        voice_mime: upInfo?.mime || mime,
      });
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Voice processing failed.");
    } finally {
      setUploading(false);
    }
  }, [context, extraContext, elapsedSec, teardown]);

  const cancel = useCallback(() => {
    const rec = recorderRef.current;
    if (rec) { try { rec.stop(); } catch (_e) { /* noop */ } }
    teardown();
    setRecording(false);
    setUploading(false);
    reset();
  }, [reset, teardown]);

  return { recording, elapsedSec, level, uploading, error, result, audioUpload, start, stop, cancel, setResult, setAudioUpload };
}
